import React from 'react';
import { 
  Users, 
  MousePointerClick, 
  DollarSign, 
  Gift,
  TrendingUp,
  Trophy,
} from 'lucide-react';

const ReferralStatsCard = ({
  clicks = 0,
  conversions = 0,
  referredUsers = 0,
  totalRevenue = 0,
  rewardEligible = false,
  rewardIssued = false,
  rewardThreshold = 500,
}) => {
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };

  const conversionRate = clicks > 0 ? ((conversions / clicks) * 100).toFixed(1) : 0;
  const rewardProgress = Math.min((totalRevenue / rewardThreshold) * 100, 100);

  return (
    <div className="space-y-4" data-testid="referral-stats-card">
      {/* Main Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <div className="bg-card rounded-xl p-4 border border-border">
          <div className="flex items-center gap-2 mb-2">
            <MousePointerClick className="w-4 h-4 text-blue-400" />
            <span className="text-sm text-muted-foreground">Clicks</span>
          </div>
          <p className="text-2xl font-bold text-foreground" data-testid="referral-stat-clicks">{clicks.toLocaleString()}</p>
        </div>

        <div className="bg-card rounded-xl p-4 border border-border">
          <div className="flex items-center gap-2 mb-2">
            <Users className="w-4 h-4 text-cyan-400" />
            <span className="text-sm text-muted-foreground">Referred Users</span>
          </div>
          <p className="text-2xl font-bold text-foreground" data-testid="referral-stat-referred-users">{referredUsers.toLocaleString()}</p>
        </div>

        <div className="bg-card rounded-xl p-4 border border-border">
          <div className="flex items-center gap-2 mb-2">
            <Trophy className="w-4 h-4 text-emerald-400" />
            <span className="text-sm text-muted-foreground">Conversions</span>
          </div>
          <p className="text-2xl font-bold text-foreground" data-testid="referral-stat-conversions">{conversions.toLocaleString()}</p>
        </div>

        <div className="bg-card rounded-xl p-4 border border-border">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-4 h-4 text-violet-400" />
            <span className="text-sm text-muted-foreground">Conversion Rate</span>
          </div>
          <p className="text-2xl font-bold text-foreground" data-testid="referral-stat-conversion-rate">{conversionRate}%</p>
        </div>

        <div className="bg-card rounded-xl p-4 border border-border">
          <div className="flex items-center gap-2 mb-2">
            <DollarSign className="w-4 h-4 text-yellow-400" />
            <span className="text-sm text-muted-foreground">Revenue Generated</span>
          </div>
          <p className="text-2xl font-bold text-emerald-400" data-testid="referral-stat-revenue">{formatCurrency(totalRevenue)}</p>
        </div>
      </div>

      {/* Reward Progress */}
      <div className={`rounded-xl p-5 border ${
        rewardIssued 
          ? 'bg-gradient-to-r from-yellow-500/10 to-amber-500/10 border-yellow-500/30'
          : rewardEligible
            ? 'bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border-emerald-500/30'
            : 'bg-card border-border'
      }`}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Gift className={`w-5 h-5 ${rewardIssued ? 'text-yellow-400' : rewardEligible ? 'text-emerald-400' : 'text-muted-foreground'}`} />
            <span className="font-medium text-foreground">Reward Progress</span>
          </div>
          {rewardIssued ? (
            <span className="flex items-center gap-1 text-sm text-yellow-400">
              <Trophy className="w-4 h-4" /> Reward Issued!
            </span>
          ) : rewardEligible ? (
            <span className="text-sm text-emerald-400">Eligible for reward!</span>
          ) : (
            <span className="text-sm text-muted-foreground">
              {formatCurrency(rewardThreshold - totalRevenue)} to go
            </span>
          )}
        </div>

        {/* Progress Bar */}
        <div className="h-2 bg-secondary rounded-full overflow-hidden mb-2">
          <div 
            className={`h-full transition-all duration-500 ${
              rewardIssued 
                ? 'bg-gradient-to-r from-yellow-400 to-amber-500'
                : rewardEligible
                  ? 'bg-gradient-to-r from-emerald-400 to-teal-500'
                  : 'bg-muted-foreground/50'
            }`}
            style={{ width: `${rewardProgress}%` }}
          />
        </div>

        <p className="text-xs text-muted-foreground">
          {formatCurrency(totalRevenue)} of {formatCurrency(rewardThreshold)} revenue threshold
        </p>
      </div>
    </div>
  );
};

export default ReferralStatsCard;
